import type { ProtocolStepDraft } from '../schemas/protocol.js';
import { isTerminalAction } from '../schemas/protocol.js';

/**
 * The JUMPS of a protocol (§7.1): every place where a step sends the session
 * to another step rather than to the next one.
 *
 * Two sources, and only two: an exit condition whose terminal action is « aller
 * à une étape », and a fallback « aller à une étape » beyond the attempts. The
 * frise draws them for the selected step (outgoing above, incoming below), the
 * card counts them (« 3 sauts »), and the tree unrolls them as branches - three
 * readers of one list, which is why the list is computed here once rather than
 * three times.
 *
 * A jump whose target step no longer exists is NOT a jump: it is a problem
 * (`protocolProblems`), and drawing an arrow to nowhere would hide the refusal
 * behind a shape. `protocolJumps` therefore keeps only the resolvable ones,
 * which is also what the mockup's `jumps()` does.
 */

export type ProtocolJumpSource =
  | { readonly kind: 'condition'; readonly conditionId: string; readonly conditionIndex: number }
  | { readonly kind: 'fallback' };

export type ProtocolJump = {
  /** The step the jump leaves. */
  readonly from: string;
  /** The step it lands on - always an existing one. */
  readonly to: string;
  readonly source: ProtocolJumpSource;
};

/** The `go-to-step` a condition ENDS on, or null when its terminal action is another. */
export function conditionTarget(condition: {
  readonly actions: readonly { readonly kind: string; readonly stepId?: string }[];
}): string | null {
  const last = condition.actions[condition.actions.length - 1];
  if (last === undefined || !isTerminalAction(last as { kind: never })) return null;
  return last.kind === 'go-to-step' && last.stepId !== undefined ? last.stepId : null;
}

export function protocolJumps(steps: readonly ProtocolStepDraft[]): ProtocolJump[] {
  const known = new Set(steps.map((step) => step.id));
  const jumps: ProtocolJump[] = [];
  for (const step of steps) {
    step.conditions.forEach((condition, conditionIndex) => {
      const to = conditionTarget(condition);
      if (to !== null && known.has(to)) {
        jumps.push({
          from: step.id,
          to,
          source: { kind: 'condition', conditionId: condition.id, conditionIndex },
        });
      }
    });
    // The rules of a FAREWELL step are ignored by the engine (issue #163, H76):
    // it plays one half-turn and closes the protocol, so a fallback stored on
    // one - the editor writes the defaults, the assistant can write anything -
    // leads nowhere, and an arrow drawn from it would be a jump never taken.
    if (
      !step.farewell &&
      step.rules.fallback.kind === 'go-to-step' &&
      known.has(step.rules.fallback.stepId)
    ) {
      jumps.push({ from: step.id, to: step.rules.fallback.stepId, source: { kind: 'fallback' } });
    }
  }
  return jumps;
}

/** How many exit conditions of a step end on « fermer l'expérience » - the card's badge (§7.1). */
export function experienceExitsOf(step: ProtocolStepDraft): number {
  return step.conditions.filter((condition) =>
    condition.actions.some((action) => action.kind === 'close-experience'),
  ).length;
}

/** A step with no direction, no objective and no condition: the frise's « étape vide ». */
export function isEmptyStep(step: ProtocolStepDraft): boolean {
  return (
    step.direction.trim() === '' && step.objectives.length === 0 && step.conditions.length === 0
  );
}
